import React from 'react';

import find from 'lodash/find';

import { useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiFormatSize } from '@lumapps/lumx/icons';
import { ReactEditor, useSlateStatic } from '@lumapps/wrex/slate';
import { ToolbarItem, Wrex } from '@lumapps/wrex/types';

import { TypographyEditor } from '../../../types';

import './index.scss';

const CLASSNAME = 'block-type-selector';

export interface BlockTypeOption {
    type: string;
    types?: string[];
    headingLevel?: number;
    label: string;
    renderer?: React.FC<{ label: string }>;
    default?: boolean;
    icon?: string;
}

export const useBlockTypeSelector = ({
    blockTypeOptions,
    iconOnly,
    withIcon,
    withVerticalIcon = true,
    withChildIcon,
    tooltipLabel,
    otherProps,
}: {
    blockTypeOptions: BlockTypeOption[];
    iconOnly?: boolean;
    withIcon?: boolean;
    withVerticalIcon?: boolean;
    withChildIcon?: boolean;
    tooltipLabel?: string;
    otherProps?: any;
}): ToolbarItem => {
    const { get } = useDataAttributes('block-type-selector');
    const { element } = useClassnames(CLASSNAME);

    const editorStatic = useSlateStatic() as ReactEditor & TypographyEditor;

    const blockTypeAtSelection = editorStatic.getBlockType();
    const blocksAtSelection = editorStatic.getBlocks() as Wrex.Element[] & { level?: number }[];
    const currentBlock = React.useMemo(
        () =>
            blockTypeOptions.find((blockOption: BlockTypeOption): boolean => {
                if (!(blockTypeAtSelection && blocksAtSelection && blocksAtSelection.length > 0)) {
                    return false;
                }

                // If block type doesn't match
                if (blockOption.type !== blockTypeAtSelection) {
                    return false;
                }

                // If the block option doesn't specify a heading level, it's a match
                if (!blockOption.headingLevel) {
                    return true;
                }

                // For heading blocks, check if the current selection has the same heading level
                return blockOption.headingLevel === blocksAtSelection[0].level;
            }),
        [blockTypeAtSelection, blockTypeOptions, blocksAtSelection],
    );

    // Disable selector if no selection or if current block is not recognized.
    const disabledBlockTypeSelector = editorStatic.selection && !currentBlock;

    const label = React.useMemo(() => {
        return currentBlock?.label ?? (find(blockTypeOptions, 'default')?.label || '');
    }, [blockTypeOptions, currentBlock?.label]);

    const icon = React.useMemo(() => {
        return withChildIcon
            ? currentBlock?.icon ?? (find(blockTypeOptions, 'default')?.icon || undefined)
            : mdiFormatSize;
    }, [blockTypeOptions, currentBlock?.icon, withChildIcon]);

    return {
        type: 'submenu',
        itemKey: 'blocktype-submenu',
        variant: iconOnly ? 'icon' : 'button',
        label,
        tooltipLabel,
        icon: withIcon ? icon : undefined,
        verticalModeProps: {
            icon: withVerticalIcon ? icon : undefined,
        },
        'aria-disabled': Boolean(disabledBlockTypeSelector),
        childrenOptions: blockTypeOptions.map((b) => {
            const Renderer = b.renderer;

            const isOptionChecked = (): boolean => {
                // If it's the default option and no block type is selected, it's checked
                if (b.default && !blockTypeAtSelection) {
                    return true;
                }

                if (!(blockTypeAtSelection && blocksAtSelection && blocksAtSelection.length > 0)) {
                    return false;
                }

                // If block type doesn't match
                if (blockTypeAtSelection !== b.type) {
                    return false;
                }

                // If the option doesn't specify a heading level, block is confirmed a match
                if (!b.headingLevel) {
                    return true;
                }

                // For heading options, check if the current selection has the same heading level
                return b.headingLevel === blocksAtSelection[0].level;
            };

            return {
                type: 'toggle-option',
                isChecked: isOptionChecked(),
                label: Renderer ? <Renderer label={b.label} /> : b.label,
                itemKey: b.headingLevel ? `${b.type}-h${b.headingLevel}` : b.type,
                icon: b.icon,
                onClick: () => {
                    // Last selection (before opening the dropdown) or on the last row in content.
                    const at = editorStatic.selection || editorStatic.getLastSelection();
                    editorStatic.changeBlockType(b.type, at);
                    if (b.headingLevel) {
                        editorStatic.changeHeadingLevel(b.headingLevel, at);
                    }
                },
                'aria-disabled': !editorStatic.isBlockTypeAllowed(b.type),
                otherProps: {
                    ...get({ element: 'option', action: b.type }),
                    className: `${otherProps?.className} ${element('menu-option')}`,
                },
            };
        }),
    };
};
